import { useEffect, useState } from 'react';
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Link, Text, useDisclosure } from '@chakra-ui/react';
import FormBase from '@/components/forms/new/FormBase';
import { validationSchema } from './consts';
import updateApprovalCode from 'API/ApprovalCode/updateApprovalCode';
import axios from 'axios';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { Button } from '@/components/v4';
import { useSelector } from 'react-redux';
import getPartnerPlans from 'API/Partner/getPartnerPlans';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import walkthroughIds from './walkthroughIds';

// Import field components
import ApprovalCodeName from './formFields/ApprovalCodeName';
import ApprovalCodePlan from './formFields/ApprovalCodePlan';
import ApprovalCodeSelectiveSyncPolicy from './formFields/ApprovalCodeSelectiveSyncPolicy';
import ApprovalCodeCode from './formFields/ApprovalCodeCode';
import ApprovalCodeAutoApprove from './formFields/ApprovalCodeAutoApprove';
import ApprovalCodeTrialDays from './formFields/ApprovalCodeTrialDays';
import ApprovalCodeValidTo from './formFields/ApprovalCodeValiddTo';
import { breadCrumbStyling } from 'components/forms/new/formStyles';

export default function ApprovalCodeEdit({
    rowData,
    setApprovalCodesAction,
    setSuccessAction,
}) {
    const [partnerPlans, setPartnerPlans] = useState(null);
    const [validToTime, setValidToTime] = useState(new Date());
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
    const [isDeleting, setIsDeleting] = useState(false);

    const currentPartner = useSelector(
        (state) => state.navigationLists?.currentPartner,
    );
    const { darkMode } = useSelector((state) => state.settings);

    useEffect(() => {
        getPartnerPlans(currentPartner)
            .then((response) => {
                const data = response.data.map((plan) => ({
                    label: plan.name,
                    value: plan.id,
                }));
                setPartnerPlans(data);
            })
            .catch((error) => {
                console.log(error.response);
            });
        if (rowData?.validTo) {
            setValidToTime(new Date(rowData.validTo));
        }
    }, [currentPartner, rowData]);

    if (!partnerPlans) return <LoadingIndicator />;

    const breadCrumbTrail = [
        { label: 'Approval Codes', path: '/approval-codes' },
        { label: 'Edit', path: '' },
    ];

    const initialValues = {
        code: rowData?.code || '',
        name: rowData?.name || '',
        trialDays: rowData?.trialDays || '',
        autoApprove: rowData?.autoApprove || 0,
        validTo: rowData?.validTo || new Date().toISOString(),
        selectiveSyncPolicy: rowData?.selectiveSyncPolicy || 0,
        planID: rowData?.planID || '',
    };

    const finalFormSubmit = async (fields, actions) => {
        setIsLoading(true);
        setError(null);
        setSuccessAction(null);

        // Hidden fields
        const hiddenFields = {
            id: rowData.id,
            partnerID: currentPartner,
        };

        // try {
        //     await validationSchema.validate(fields, { abortEarly: false });
        // } catch (validationErrors) {
        //     setIsLoading(false);
        //     return;
        // }

        updateApprovalCode({ ...fields, ...hiddenFields })
            .then((response) => {
                if (response.status === 200) {
                    setSuccessAction('Edit');
                    setApprovalCodesAction('Get');
                    setTimeout(() => {
                        setSuccessAction(null);
                    }, 6000);
                } else {
                    setError(response.response.data.message);
                }
            })
            .catch((error) => {
                if (error?.response?.data?.message) {
                    setError(error.response.data.message);
                } else {
                    setError(
                        'Something went wrong when editing this approval code.',
                    );
                }
            })
            .finally(() => {
                setIsLoading(false);
                actions.setSubmitting(false);
            });
    };

    const { createMode } = walkthroughIds;

    return (
        <>
            <Breadcrumb sx={breadCrumbStyling}>
                {breadCrumbTrail?.map((crumb, index) => (
                    <BreadcrumbItem key={index}>
                        {Array.isArray(crumb.path) ? (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path[0]}
                                state={crumb.path[1]}
                                onClick={index === 0 ? () => setApprovalCodesAction('Get') : undefined}
                            >
                                {crumb.label}
                            </BreadcrumbLink>
                        ) : (
                            <BreadcrumbLink
                                as={Link}
                                to={crumb.path}
                                onClick={index === 0 ? () => setApprovalCodesAction('Get') : undefined}
                            >
                                {crumb.label}
                            </BreadcrumbLink>
                        )}
                    </BreadcrumbItem>
                ))}
            </Breadcrumb>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    // marginTop: '15px',
                    // marginBottom: '15px',
                }}>
                Edit Approval Code
            </Text>
            <FormBase
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                validationSchema={validationSchema}
                ufBackBtn={createMode.ufBackBtn}
                ufSaveBtn={createMode.ufEditBtn}
                customClick={true}
                extraButton={
                    <Box w="100%" display="flex" justifyContent="flex-end">
                        <Button
                            w={142}
                            colorScheme="red"
                            onClick={onDeleteModalOpen}
                            isDisabled={isDeleting || isLoading}>
                            Delete
                        </Button>
                    </Box>
                }
                onBack={() => setApprovalCodesAction('Get')}
            >
                {({ errors = {}, touched = {} }) => [
                        <ApprovalCodeName
                            key="name"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApprovalCodePlan
                            key="plan"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            partnerPlans={partnerPlans}
                        />,
                        <ApprovalCodeSelectiveSyncPolicy
                            key="selectiveSync"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApprovalCodeCode
                            key="code"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApprovalCodeAutoApprove
                            key="autoApprove"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApprovalCodeTrialDays
                            key="trialDays"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                        />,
                        <ApprovalCodeValidTo
                            key="validTo"
                            errors={errors}
                            touched={touched}
                            darkMode={darkMode}
                            validToTime={validToTime}
                            setValidToTime={setValidToTime}
                        />,
                        error && (
                            <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )
                ].filter(Boolean)}
            </FormBase>

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={onDeleteModalClose}
                approvalCodeName={rowData?.name}
                onConfirm={async () => {
                    try {
                        setIsDeleting(true);
                        await axios.delete(`/approvalCode/${rowData.id}`);
                        setApprovalCodesAction('Get');
                    } catch (err) {
                        setError(err?.response?.data?.message || 'Failed to delete approval code');
                    } finally {
                        setIsDeleting(false);
                    }
                }}
                isDeleting={isDeleting}
            />
        </>
    );
}